import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, Search, X, Check, Copy, Inbox as InboxIcon } from "lucide-react";
import { cn, copyText } from "@/lib/utils";
import { avatarHue, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/* ---------- Page scaffolding ---------- */

export function PageHeader({ title, description, actions, breadcrumb, className }) {
  return (
    <div className={cn("mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between", className)}>
      <div className="min-w-0">
        {breadcrumb && <div className="mb-1 text-xs text-muted-foreground">{breadcrumb}</div>}
        <h1 className="page-title">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Section({ title, description, actions, children, className, bodyClassName }) {
  return (
    <section className={cn("card-surface", className)}>
      {(title || actions) && (
        <header className="flex flex-col gap-2 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {title && <h2 className="font-display text-[17px] font-bold">{title}</h2>}
            {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={cn("p-5", bodyClassName)}>{children}</div>
    </section>
  );
}

/* ---------- Stats ---------- */

export function StatCard({ label, value, hint, delta, icon: Icon, tone = "default", to, loading }) {
  const toneCls = {
    default: "bg-secondary text-foreground",
    primary: "bg-accent text-accent-foreground",
    success: "bg-accent text-accent-foreground",
    warning: "bg-amber-50 text-amber-700",
    danger: "bg-red-50 text-red-700",
    info: "bg-sky-50 text-sky-700",
    violet: "bg-violet-50 text-violet-700",
  }[tone];
  const body = (
    <div className="card-surface lift flex items-start gap-4 p-4">
      {Icon && (
        <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", toneCls)}>
          <Icon className="h-5 w-5" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        {loading ? (
          <Skeleton className="mt-1.5 h-7 w-20" />
        ) : (
          <p className="mt-0.5 text-2xl font-semibold tracking-tight">{value}</p>
        )}
        {(hint || delta != null) && (
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            {delta != null && <DeltaPill value={delta} />}
            {hint}
          </p>
        )}
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
}

export function DeltaPill({ value }) {
  if (value == null || Number.isNaN(value)) return null;
  const up = value > 0;
  const flat = value === 0;
  return (
    <span
      className={cn(
        "rounded-md px-1.5 py-0.5 text-[11px] font-medium",
        flat ? "bg-secondary text-muted-foreground" : up ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700",
      )}
    >
      {flat ? "0%" : `${up ? "+" : ""}${value}%`}
    </span>
  );
}

/* ---------- Status / badges ---------- */

const STATUS_VARIANTS = {
  connected: "success", not_configured: "warning", error: "danger",
  active: "success", inactive: "neutral", unsubscribed: "danger",
  open: "info", resolved: "success", snoozed: "neutral",
  draft: "neutral", scheduled: "info", queued: "info", sending: "info", paused: "warning",
  completed: "success", completed_with_failures: "warning", cancelled: "neutral",
  pending: "neutral", sent: "info", delivered: "success", read: "success", failed: "danger", skipped: "neutral", received: "neutral",
  APPROVED: "success", PENDING: "warning", REJECTED: "danger", PAUSED: "warning", DISABLED: "danger",
  lead: "info", prospect: "violet", customer: "success", churned: "neutral",
  won: "success", lost: "danger", done: "success",
  owner: "violet", admin: "info", manager: "success", agent: "neutral",
  whatsapp: "success", instagram: "violet",
};

const STATUS_LABELS = {
  not_configured: "Not connected", completed_with_failures: "Completed with failures", opted_in: "Opted in",
};

export function StatusBadge({ status, label, className, dot = true }) {
  const variant = STATUS_VARIANTS[status] || "neutral";
  const text = label || STATUS_LABELS[status] || String(status || "").replace(/_/g, " ");
  return (
    <Badge variant={variant} className={cn("capitalize", className)}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />}
      {text}
    </Badge>
  );
}

export function Avatar({ name, size = "md", className, src }) {
  const sz = { xs: "h-6 w-6 text-[10px]", sm: "h-8 w-8 text-xs", md: "h-10 w-10 text-sm", lg: "h-14 w-14 text-lg" }[size];
  const hue = avatarHue(name);
  if (src) return <img src={src} alt={name} className={cn("rounded-full object-cover", sz, className)} />;
  return (
    <div
      className={cn("flex shrink-0 items-center justify-center rounded-full font-semibold", sz, className)}
      style={{ background: `hsl(${hue} 60% 92%)`, color: `hsl(${hue} 50% 32%)` }}
      aria-hidden
    >
      {initials(name)}
    </div>
  );
}

/* ---------- Empty / loading ---------- */

export function EmptyState({ icon: Icon = InboxIcon, title, description, action, className, compact }) {
  return (
    <div className={cn("flex flex-col items-center justify-center rounded-[18px] border border-dashed border-border bg-secondary/40 text-center", compact ? "px-4 py-8" : "px-6 py-14", className)}>
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-card shadow-card">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <p className="font-display text-[15px] font-bold">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Spinner({ className }) {
  return <Loader2 className={cn("h-4 w-4 animate-spin text-muted-foreground", className)} />;
}

export function PageLoader({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
      <Spinner className="mr-2" /> {label}
    </div>
  );
}

export function TableSkeleton({ rows = 5, cols = 5 }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-3">
          {Array.from({ length: cols }).map((__, j) => (
            <Skeleton key={j} className={cn("h-5", j === 0 ? "w-1/4" : "flex-1")} />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ---------- Inputs ---------- */

export function SearchInput({ value, onChange, placeholder = "Search…", className, autoFocus }) {
  return (
    <div className={cn("relative", className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-9 pr-8" autoFocus={autoFocus} />
      {value && (
        <button type="button" onClick={() => onChange("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-secondary" aria-label="Clear">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function TagInput({ value = [], onChange, placeholder = "Add tag and press Enter", suggestions = [] }) {
  const [draft, setDraft] = useState("");
  const add = (t) => {
    const tag = t.trim();
    if (!tag || value.includes(tag)) return setDraft("");
    onChange([...value, tag]);
    setDraft("");
  };
  return (
    <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-xl border border-border bg-card px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring">
      {value.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-0.5 text-xs font-medium text-accent-foreground">
          {t}
          <button type="button" onClick={() => onChange(value.filter((x) => x !== t))} className="text-muted-foreground hover:text-foreground" aria-label={`Remove ${t}`}>
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(draft); }
          if (e.key === "Backspace" && !draft && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={() => draft && add(draft)}
        placeholder={value.length ? "" : placeholder}
        list={suggestions.length ? "tag-suggestions" : undefined}
        className="min-w-[120px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
      />
      {suggestions.length > 0 && (
        <datalist id="tag-suggestions">
          {suggestions.map((s) => <option key={s} value={s} />)}
        </datalist>
      )}
    </div>
  );
}

export function Field({ label, hint, error, children, className, required }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label className="text-[13px] font-medium text-foreground">
          {label} {required && <span className="text-destructive">*</span>}
        </label>
      )}
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export function SegmentedControl({ value, onChange, options, className, size = "sm" }) {
  return (
    <div className={cn("inline-flex items-center rounded-full bg-secondary p-1", className)} role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "flex items-center gap-1.5 rounded-full font-semibold transition-colors",
            size === "sm" ? "px-3 py-1 text-xs" : "px-4 py-1.5 text-sm",
            value === o.value ? "bg-foreground text-white" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {o.icon && <o.icon className="h-3.5 w-3.5" />}
          {o.label}
          {o.count != null && <span className={cn("rounded-full px-1.5 text-[10px]", value === o.value ? "bg-white/20" : "bg-card")}>{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ---------- Pagination ---------- */

export function Pagination({ page, pages, total, pageSize, onPage, className }) {
  if (!pages || pages <= 1) {
    return total != null ? <p className={cn("text-xs text-muted-foreground", className)}>{total} result{total === 1 ? "" : "s"}</p> : null;
  }
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);
  return (
    <div className={cn("flex items-center justify-between gap-3 text-xs text-muted-foreground", className)}>
      <span>{start}–{end} of {total}</span>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="icon-sm" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page"><ChevronLeft className="h-4 w-4" /></Button>
        <span className="px-2">Page {page} / {pages}</span>
        <Button variant="outline" size="icon-sm" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Next page"><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

/* ---------- Dialogs ---------- */

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel = "Confirm", destructive, onConfirm, loading, children }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); onConfirm(); }}
            disabled={loading}
            className={cn(destructive && "bg-destructive text-destructive-foreground hover:bg-destructive/90")}
          >
            {loading ? <Spinner className="text-current" /> : null}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ---------- Misc ---------- */

export function CopyButton({ text, label = "Copy", size = "xs", className }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      className={className}
      onClick={async () => {
        if (await copyText(text)) {
          setCopied(true);
          clearTimeout(timer.current);
          timer.current = setTimeout(() => setCopied(false), 1500);
        }
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : label}
    </Button>
  );
}

export function KeyValue({ items, className }) {
  return (
    <dl className={cn("divide-y divide-border rounded-xl border border-border bg-card", className)}>
      {items.map((it) => (
        <div key={it.label} className="flex items-start justify-between gap-4 px-4 py-2.5 text-sm">
          <dt className="shrink-0 text-muted-foreground">{it.label}</dt>
          <dd className={cn("min-w-0 text-right", it.mono && "font-mono text-xs", it.className)}>
            {it.value == null || it.value === "" ? <span className="text-muted-foreground">—</span> : it.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function ProgressBar({ value, className, tone = "primary" }) {
  const toneCls = { primary: "bg-primary", success: "bg-emerald-500", warning: "bg-amber-500", danger: "bg-red-500", info: "bg-sky-500" }[tone];
  return (
    <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-secondary", className)}>
      <div className={cn("h-full rounded-full transition-all", toneCls)} style={{ width: `${Math.max(0, Math.min(100, value || 0))}%` }} />
    </div>
  );
}

export function Kbd({ children }) {
  return <kbd className="rounded border border-border bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">{children}</kbd>;
}

export function InlineAlert({ tone = "info", title, children, className, icon: Icon }) {
  const cls = {
    info: "border-sky-200 bg-sky-50 text-sky-900",
    warning: "border-amber-200 bg-amber-50 text-amber-900",
    danger: "border-red-200 bg-red-50 text-red-900",
    success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  }[tone];
  return (
    <div className={cn("flex gap-3 rounded-xl border px-3.5 py-3 text-sm", cls, className)}>
      {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0" />}
      <div className="min-w-0">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={cn(title && "mt-0.5", "text-[13px] opacity-90")}>{children}</div>}
      </div>
    </div>
  );
}
